/**
 * Helper functions for making authenticated API calls
 */
import { NEXTAUTH_API_URL, API_URL } from "@/lib/config";
// Type definitions
type JsonResponse = Record<string, unknown>;

export type publishState = "Publish" | "pending" | "approved" | "rejected" | "accepted";

export interface publishResponse extends JsonResponse {
  approvalStatus?: publishState;
  isPublish?: boolean;
  updatedAt: string;
}

// Error response type
export interface ApiErrorResponse {
  status: number;
  message: string;
  error?: string;
  isError: true;
}

// Utility functions for API calls
// Note: Request deduplication is now handled by React Query
// Request deduplication cache for in-flight requests (This prevents duplicate API calls for the same URL)
const inFlightRequests = new Map<string, Promise<any>>();

// Utility function to clear cached 404 errors
export function clearCached404Error(noteId: string) {
  window.localStorage.removeItem(`404-error-${noteId}`);
  window.localStorage.removeItem(`last-api-check-${noteId}`);
}

function resolveUrl(url: string): string {
  // Auth and generate routes stay on the Next.js frontend server
  if (url.startsWith("/api/auth") || url.startsWith("/api/generate")) return url;
  // All other /api/* calls go directly to the backend
  if (url.startsWith("/api/")) return `${API_URL}${url}`;
  return url;
}

// Function to make an authenticated fetch request
//
// SECURITY NOTES (CRIT-01 / CRIT-08):
//   - We DO NOT read `document.cookie` and mirror the NextAuth JWE into an
//     `Authorization: Bearer ...` header. That pattern defeated the cookie's
//     HttpOnly attribute (any third-party JS could intercept the header).
//     Cookies travel automatically because of credentials:"include" below.
//   - We DO NOT send `x-user-email` / `x-user-name` / `x-user-image`. The
//     backend AuthGuard no longer trusts those headers; identity is derived
//     exclusively from the NextAuth session cookie.
// Socket id of THIS tab, published by SocketContext on connect.
//
// Module scope, deliberately NOT localStorage: the point is to identify the
// TAB, and localStorage is shared across tabs of the same browser — storing it
// there would make two tabs indistinguishable and defeat the self-echo skip
// it exists for.
let activeSocketId: string | null = null;

/** Called by SocketContext on connect/disconnect. */
export function setActiveSocketId(id: string | null): void {
  activeSocketId = id || null;
}

// Capture token for the screenshot worker's headless browser on /capture/*.
// The worker has no session cookie, so the backend authorises these reads by
// this header instead. Set once by the capture route from its URL.
let captureToken: string | null = null;
export function setCaptureToken(token: string | null): void {
  captureToken = token;
}

export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const headers = new Headers(options.headers || {});

  if (captureToken) headers.set("x-capture-token", captureToken);

  // Let the server stamp its broadcast with the tab that caused it, so this
  // tab can ignore its own echo. Without it, a local-first create triggers a
  // refetch of server state that has not been synced yet — the new item
  // vanishes and reappears (the create/disappear/recreate blink).
  if (activeSocketId) headers.set("x-socket-id", activeSocketId);

  // Inject workspace ID header — the workspace cookie uses SameSite=Strict and is
  // stripped when requests go cross-origin to API_URL, so we read from localStorage instead.
  // The backend cross-checks workspace membership server-side; this is just a hint.
  const cachedWorkspace = localStorage.getItem("cached_workspace");
  if (cachedWorkspace) {
    try {
      const ws = JSON.parse(cachedWorkspace);
      const wsId = ws._id || ws.id;
      if (wsId) headers.set("x-workspace-id", String(wsId));
    } catch {
      // ignore malformed cache
    }
  }

  return fetch(resolveUrl(url), {
    ...options,
    headers,
    credentials: "include",
  });
}

function getRedirectUrl() {
  return typeof window !== "undefined" ? window.location.href : "";
}

export function isPublicRoute() {
  if (typeof window === "undefined") return false;
  // Use URL parsing to normalize the path and prevent path traversal bypasses
  // (e.g., /public/note/../private/admin)
  try {
    const normalized = new URL(window.location.href).pathname;
    return /^\/public\/note\/[^/]+\/?$/.test(normalized) || /^\/capture\/[^/]+\/?$/.test(normalized);
  } catch {
    return false;
  }
}

// Get function with auth
export async function getWithAuth<T = JsonResponse | publishResponse | unknown>(
  url: string,
  options: RequestInit = {},
): Promise<T | ApiErrorResponse> {
  // Create a unique key for this request (URL + method)
  const requestKey = `GET:${url}`;
  
  // Check if there's already an in-flight request for this URL
  const existingRequest = inFlightRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest as Promise<T | ApiErrorResponse>;
  }
  
  // Create a new request promise
  const requestPromise = (async () => {
    try {
      const response = await fetchWithAuth(url, {
        ...options,
        method: "GET",
      });

    // Handle authentication errors
    if (response.status === 401) {
      if (isPublicRoute()) {
        return { status: 401, message: "Unauthorized", isError: true } as ApiErrorResponse;
      }
      window.location.href = `${NEXTAUTH_API_URL || "http://localhost:3001"}?redirect=${encodeURIComponent(getRedirectUrl())}`;
      throw new Error("Unauthorized: Redirecting to login...");
    }

    // Handle different error status codes
    if (!response.ok) {
      // Oversized uploads return 413. These often come from the proxy/gateway
      // with a non-JSON (HTML/empty) body, so the JSON branch below can't
      // produce a useful message — special-case it up front.
      if (response.status === 413) {
        return {
          status: 413,
          message: "File is too large. Please upload a smaller file.",
          isError: true,
        } as ApiErrorResponse;
      }

      let errorMessage = "An error occurred";

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If JSON parsing fails, use default message
        if (response.status === 403) errorMessage = "You don't have permission to access this resource";
        if (response.status === 404) errorMessage = "The resource you're looking for was not found";
        if (response.status === 500) errorMessage = "Internal server error occurred";
        if (response.status >= 500) errorMessage = "Server error occurred. Please try again later";
      }

      return {
        status: response.status,
        message: errorMessage,
        isError: true,
      } as ApiErrorResponse;
    }

      return response.json() as Promise<T>;
    } catch (error) {
      console.error("Network or parsing error:", error);
      return {
        status: 0,
        message: "Network error. Please check your connection and try again.",
        error: error instanceof Error ? error.message : "Unknown error",
        isError: true,
      } as ApiErrorResponse;
    } finally {
      // Remove from cache when request completes (success or failure)
      inFlightRequests.delete(requestKey);
    }
  })();
  
  // Store the promise in cache
  inFlightRequests.set(requestKey, requestPromise);
  
  return requestPromise;
}

// Post function with auth
export async function postWithAuth<T = JsonResponse | publishResponse | any, D = Record<string, any>>(
  url: string,
  data: D,
  options: RequestInit = {},
): Promise<T | ApiErrorResponse> {
  const isBinary = data instanceof ArrayBuffer || data instanceof Blob;
  try {
    const response = await fetchWithAuth(url, {
      ...options,
      method: "POST",
      headers: {
        ...(isBinary ? {} : { "Content-Type": "application/json" }),
        ...(options.headers || {}),
      },
      body: isBinary ? data : JSON.stringify(data),
    });

    // Handle authentication errors
    if (response.status === 401) {
      // Capture mode is read-only by design: the worker's browser must never
      // be bounced to login by a refused write, or the frame is lost.
      if (captureToken || isPublicRoute()) {
        return { status: 401, message: "Unauthorized", isError: true } as ApiErrorResponse;
      }
      window.location.href = `${NEXTAUTH_API_URL || "http://localhost:3001"}?redirect=${encodeURIComponent(getRedirectUrl())}`;
      throw new Error("Unauthorized: Redirecting to login...");
    }

    // Handle different error status codes
    if (!response.ok) {
      // Oversized uploads return 413. These often come from the proxy/gateway
      // with a non-JSON (HTML/empty) body, so the JSON branch below can't
      // produce a useful message — special-case it up front.
      if (response.status === 413) {
        return {
          status: 413,
          message: "File is too large. Please upload a smaller file.",
          isError: true,
        } as ApiErrorResponse;
      }

      let errorMessage = "An error occurred";

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If JSON parsing fails, use default message
        if (response.status === 403) errorMessage = "You don't have permission to perform this action";
        if (response.status === 404) errorMessage = "The resource you're trying to update was not found";
        if (response.status === 500) errorMessage = "Internal server error occurred";
        if (response.status >= 500) errorMessage = "Server error occurred. Please try again later";
      }

      return {
        status: response.status,
        message: errorMessage,
        isError: true,
      } as ApiErrorResponse;
    }

    // Some APIs return no content
    if (response.status === 204) {
      return { success: true } as unknown as T;
    }

    return response.json() as Promise<T>;
  } catch (error) {
    console.error("Network or parsing error:", error);
    return {
      status: 0,
      message: "Network error. Please check your connection and try again.",
      error: error instanceof Error ? error.message : "Unknown error",
      isError: true,
    } as ApiErrorResponse;
  }
}

// Put function with auth
export async function putWithAuth<T = JsonResponse, D = Record<string, unknown>>(
  url: string,
  data: D,
  options: RequestInit = {},
): Promise<T | ApiErrorResponse> {
  try {
    const response = await fetchWithAuth(url, {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
      body: JSON.stringify(data),
    });

    // Handle authentication errors
    if (response.status === 401) {
      // Capture mode is read-only by design: the worker's browser must never
      // be bounced to login by a refused write, or the frame is lost.
      if (captureToken || isPublicRoute()) {
        return { status: 401, message: "Unauthorized", isError: true } as ApiErrorResponse;
      }
      window.location.href = `${NEXTAUTH_API_URL || "http://localhost:3001"}?redirect=${encodeURIComponent(getRedirectUrl())}`;
      throw new Error("Unauthorized: Redirecting to login...");
    }

    // Handle different error status codes
    if (!response.ok) {
      // Oversized uploads return 413. These often come from the proxy/gateway
      // with a non-JSON (HTML/empty) body, so the JSON branch below can't
      // produce a useful message — special-case it up front.
      if (response.status === 413) {
        return {
          status: 413,
          message: "File is too large. Please upload a smaller file.",
          isError: true,
        } as ApiErrorResponse;
      }

      let errorMessage = "An error occurred";

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If JSON parsing fails, use default message
        if (response.status === 403) errorMessage = "You don't have permission to perform this action";
        if (response.status === 404) errorMessage = "The resource you're trying to update was not found";
        if (response.status === 500) errorMessage = "Internal server error occurred";
        if (response.status >= 500) errorMessage = "Server error occurred. Please try again later";
      }

      return {
        status: response.status,
        message: errorMessage,
        isError: true,
      } as ApiErrorResponse;
    }

    // Some APIs return no content
    if (response.status === 204) {
      return { success: true } as unknown as T;
    }

    return response.json() as Promise<T>;
  } catch (error) {
    console.error("Network or parsing error:", error);
    return {
      status: 0,
      message: "Network error. Please check your connection and try again.",
      error: error instanceof Error ? error.message : "Unknown error",
      isError: true,
    } as ApiErrorResponse;
  }
}

// Delete function with auth
export async function deleteWithAuth<T = unknown>(
  url: string,
  options: RequestInit = {},
): Promise<T | ApiErrorResponse> {
  try {
    const response = await fetchWithAuth(url, {
      ...options,
      method: "DELETE",
      headers: {
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
    });

    // Handle authentication errors
    if (response.status === 401) {
      // Capture mode is read-only by design: the worker's browser must never
      // be bounced to login by a refused write, or the frame is lost.
      if (captureToken || isPublicRoute()) {
        return { status: 401, message: "Unauthorized", isError: true } as ApiErrorResponse;
      }
      window.location.href = `${NEXTAUTH_API_URL || "http://localhost:3001"}?redirect=${encodeURIComponent(getRedirectUrl())}`;
      throw new Error("Unauthorized: Redirecting to login...");
    }

    // Handle different error status codes
    if (!response.ok) {
      // Oversized uploads return 413. These often come from the proxy/gateway
      // with a non-JSON (HTML/empty) body, so the JSON branch below can't
      // produce a useful message — special-case it up front.
      if (response.status === 413) {
        return {
          status: 413,
          message: "File is too large. Please upload a smaller file.",
          isError: true,
        } as ApiErrorResponse;
      }

      let errorMessage = "An error occurred";

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If JSON parsing fails, use default message
        if (response.status === 403) errorMessage = "You don't have permission to perform this action";
        if (response.status === 404) errorMessage = "The resource you're trying to delete was not found";
        if (response.status === 500) errorMessage = "Internal server error occurred";
        if (response.status >= 500) errorMessage = "Server error occurred. Please try again later";
      }

      return {
        status: response.status,
        message: errorMessage,
        isError: true,
      } as ApiErrorResponse;
    }

    // Some APIs return no content
    if (response.status === 204) {
      return { success: true } as unknown as T;
    }

    return response.json() as Promise<T>;
  } catch (error) {
    console.error("Network or parsing error:", error);
    return {
      status: 0,
      message: "Network error. Please check your connection and try again.",
      error: error instanceof Error ? error.message : "Unknown error",
      isError: true,
    } as ApiErrorResponse;
  }
}
